import { InteractionStatus } from '@azure/msal-browser';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import i18n, { SettingsType } from 'utils/i18n';
import { useUserData } from 'utils/user';
import Button from './button';
import ClaimBadgeForm from './claim-badge-form';
import Modal from './modal';
import Link from 'next/link';

export type StartAssessmentButtonProps = {
  assessmentId: string;
  setAnswers?: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>;
  status: AssessmentStatus | null;
  setStatus: (status: AssessmentStatus) => void;
  percentageCorrect?: number | null;
};

export const StartAssessmentButton: React.FC<StartAssessmentButtonProps> = ({
  assessmentId,
  // setAnswers,
  status,
  setStatus,
  percentageCorrect,
}) => {
  const [loading, setLoading] = useState(false);
  const { loggedIn, login, inProgress, achievements, saveAction } = useUserData();
  const [showModal, setShowModal] = useState<'None' | 'Login' | 'Congratulation' | 'ClaimForm'>('None');

  const { locale, asPath, query, push } = useRouter(); // eslint-disable-line
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType; // eslint-disable-line

  const completed = query.step === 'completed';

  const goToSlide = (quizId: string): void => {
    push(`${asPath.split('?')[0]}?step=${quizId}`);
  };

  const fetchStatus = async (assId): Promise<void> => {
    setLoading(true);
    const res = await fetch(`/api/get-assessment-status?assessmentId=${assId}`);
    if (res.status === 200) {
      const json: AssessmentStatus = await res.json();
      setStatus(json);
      // console.log('json', json);
      // if (json.answers) {
      //   setAnswers(json.answers);
      // }
    }

    setLoading(false);
  };

  const handleOnClaim = (): void => {
    fetchStatus(assessmentId);
    setShowModal('None');
  };

  useEffect(() => {
    if (status?.status !== 'Passed' && completed) {
      setShowModal('Congratulation');
      // Set action
      const moduleCompleted = !!achievements.find(
        (ach) => ach.contentId === assessmentId && ach.type === 'AssessmentComplete'
      );
      if (!moduleCompleted) {
        const moduleCompleteAction: UserAchievement = {
          type: 'AssessmentComplete',
          contentId: assessmentId,
        };
        saveAction(moduleCompleteAction);
      }
    }
  }, [status, completed]); // eslint-disable-line

  useEffect(() => {
    if (loggedIn) {
      fetchStatus(assessmentId);
    }
  }, [assessmentId, loggedIn, completed, query.step]); // eslint-disable-line

  if (loading) {
    return null;
  }

  if (inProgress !== InteractionStatus.None) {
    return null;
  }

  if (!loggedIn) {
    return (
      <StyledAssessmentButton>
        <Modal
          visible={showModal === 'Login'}
          headline={dictionary.assessmentSignInToStart}
          onClose={() => setShowModal('None')}
        >
          <div className="buttons">
            <Button mode="primary" size="small" onClick={() => login()}>
              {dictionary.login}
            </Button>
            <Button size="small" onClick={() => setShowModal('None')}>
              {dictionary.cancel}
            </Button>
          </div>
        </Modal>
        <Button mode="primary" alignIcon="right" iconName="chevron-right" onClick={() => setShowModal('Login')}>
          {dictionary.assessmentStartBtn}
        </Button>
      </StyledAssessmentButton>
    );
  }

  if (!status) {
    return null;
  }

  if (status.claimed) {
    return (
      <>
        <StyledAssessmentButton>
          <Button mode="primary" iconName="chevron-right" alignIcon="right" linkProps={{ href: `/browse` }}>
            {dictionary.exploreOtherModulesAndLearningPaths}
          </Button>
        </StyledAssessmentButton>
        <StyledCredlyLink href="https://info.credly.com/" target="_blank">
          {dictionary.viewBadgeCredly}
        </StyledCredlyLink>
      </>
    );
  }

  if (status.status === 'NeverStarted' && !query.step) {
    return (
      <StyledAssessmentButton>
        <Button
          mode="primary"
          alignIcon="right"
          iconName="chevron-right"
          onClick={() => {
            if (status.firstStep) {
              goToSlide(status.firstStep);
            }
          }}
        >
          {dictionary.assessmentStartBtn}
        </Button>
      </StyledAssessmentButton>
    );
  }
  if (status.status === 'InProgress' && !query.step) {
    return (
      <StyledAssessmentButton>
        <Button
          mode="primary"
          alignIcon="right"
          iconName="chevron-right"
          onClick={() => {
            if (status.firstStep) {
              goToSlide(status.firstStep);
            }
          }}
        >
          {dictionary.assessmentContinueBtn}: {status.firstStep}
        </Button>
      </StyledAssessmentButton>
    );
  }
  if (status.status === 'Failed' && !query.step && percentageCorrect === null) {
    return (
      <StyledAssessmentButton>
        <Button
          mode="primary"
          alignIcon="right"
          iconName="chevron-right"
          onClick={() => {
            if (status.firstStep) {
              goToSlide(status.firstStep);
            }
          }}
        >
          {dictionary.assessmentStartBtn}
        </Button>
      </StyledAssessmentButton>
    );
  }
  if (status.status === 'Passed') {
    return (
      <StyledAssessmentButton>
        <Button mode="primary" size="small" onClick={() => setShowModal('ClaimForm')}>
          {dictionary.assessmentClaimFormModalBtn}
        </Button>
        <p>
          <Link href="/browse">
            <span>{dictionary.exploreOtherModulesAndLearningPaths}</span>
          </Link>
        </p>
        <Modal visible={showModal === 'ClaimForm'} headline={dictionary.assessmentClaimFormModalBtn} onClose={() => setShowModal('None')}>
          <ClaimBadgeForm assessmentId={assessmentId} onComplete={handleOnClaim} />
        </Modal>
        {completed && (
          <Modal
            visible={showModal === 'Congratulation'}
            headline={dictionary.assessmentCongratsModalHeadline}
            onClose={() => setShowModal('None')}
          >
            <div className="assessment-modal-text">
              <p>{dictionary.assessmentPassedDone}</p>
              <p>{dictionary.assessmentTheWorldKnow}</p>
            </div>

            <div className="buttons">
              <Button mode="primary" size="small" onClick={() => setShowModal('ClaimForm')}>
                {dictionary.assessmentClaimFormModalBtn}
              </Button>
              <Button size="small" onClick={() => setShowModal('None')}>
                {dictionary.notNow}
              </Button>
            </div>
          </Modal>
        )}
      </StyledAssessmentButton>
    );
  }
  if (status.status === 'Failed' && !query.step && percentageCorrect !== null) {
    return (
      <StyledAssessmentButton>
        <Button
          mode="primary"
          alignIcon="right"
          iconName="chevron-right"
          onClick={() => {
            if (status.firstStep) {
              goToSlide(status.firstStep);
            }
          }}
        >
          {dictionary.assessmentDidntPassBtn}
        </Button>
      </StyledAssessmentButton>
    );
  }

  return null;
};

const StyledAssessmentButton = styled.div`
  padding: 1rem 0 0 0;

  > h3 {
    margin: 0.5rem 0;
  }

  > p {
    margin: 0.5rem 0;
  }

  > button {
    margin: 1rem 0 0 0;
  }

  .assessment-modal-text {
    margin: 1rem 0;

    p {
      margin: 0;
    }
  }
`;

const StyledCredlyLink = styled.a`
  margin-top: 0.5rem;
  display: inline-block;
`;

export default StartAssessmentButton;
